import * as React from "react";
import { cn, Collapsible, CollapsibleContent, CollapsibleTrigger, Markdown } from "@aisquare/ui";
import { ChevronDownIcon, CodeXmlIcon, LinkIcon } from "lucide-react";
import { demoSourceForTitle, entryForId, layerMetaFor, type DemoEntry } from "./registry";
import { extractVariantSource } from "./extractSource";
import { Eyebrow, ImportLine, LayerBadge, NewBadge } from "./chrome";

/**
 * A component's page.
 *
 * `<Section>` is what each of the 118 demo files renders, so it is the page
 * body, not a band on a shared scroll: title block, the copyable import, a
 * Preview / Code switch, and the demo's own `<VariantBlock>` examples. The prop
 * contract (`id` / `title` / `description` / children) is unchanged, so no demo
 * file had to be touched to move to per-component pages.
 *
 * Layer badge, "New" tag and category all resolve from the registry by `id` —
 * demos pass none of it, so the taxonomy stays defined in exactly one place.
 */

interface SectionContextValue {
  /** Section id, used to namespace each example's anchor. */
  id: string;
  /** Raw demo source, so a `<VariantBlock>` can slice out its own snippet. */
  source: string | undefined;
}

const SectionContext = React.createContext<SectionContextValue | undefined>(undefined);

/** Anchor id for one example within a component page. */
function anchorFor(sectionId: string, label: string): string {
  return `${sectionId}--${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}`;
}

type Tab = "preview" | "code";

interface SectionProps {
  id: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}

export function Section({ id, title, description, children }: SectionProps) {
  const entry = entryForId(id);
  const layer = entry ? layerMetaFor(entry.layer) : undefined;
  const source = demoSourceForTitle(title);
  const [tab, setTab] = React.useState<Tab>("preview");

  // Labels of the top-level examples, for the jump row. Reading them off the
  // children keeps the row derived rather than declared — a demo adds a
  // <VariantBlock> and the row picks it up. A block nested deeper than the
  // top level simply doesn't appear, which is the harmless failure.
  const labels = React.useMemo(
    () =>
      React.Children.toArray(children)
        .filter(
          (child): child is React.ReactElement<VariantBlockProps> =>
            React.isValidElement(child) && child.type === VariantBlock,
        )
        .map((child) => child.props.label),
    [children],
  );

  const ctx = React.useMemo<SectionContextValue>(() => ({ id, source }), [id, source]);

  // Most layers name their catch-all category after the layer itself
  // ("Primitives · brand" → "Primitives"), which would print the same word
  // twice in the eyebrow. Show the category only when it adds something.
  const layerName = layer?.label.split(" · ")[0];
  const category =
    entry && entry.category.toLowerCase() !== layerName?.toLowerCase() ? entry.category : undefined;

  return (
    <article id={id} className="min-w-0">
      <header className="mb-7">
        <Eyebrow className="mb-2">
          {layer ? layer.label : "Component"}
          {category ? (
            <>
              <span className="mx-1.5 opacity-40">/</span>
              {category}
            </>
          ) : null}
        </Eyebrow>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {/* Merriweather (--font-display) — the brand's editorial face, which
              is what makes a page here read as AISquare rather than as another
              shadcn docs surface. */}
          <h1 className="font-display text-heading text-foreground">{title}</h1>
          {layer ? <LayerBadge meta={layer} /> : null}
          {entry ? <NewBadge since={entry.since} /> : null}
          <a
            href={`#${id}`}
            aria-label={`Permalink to ${title}`}
            title={`Permalink · added in ${entry?.since ?? "—"}`}
            className="inline-flex items-center gap-1 font-mono text-caption text-muted-foreground transition-colors hover:text-accent"
          >
            <LinkIcon className="size-3" />
            {id}
          </a>
        </div>

        {description ? (
          <p className="mt-3 max-w-[68ch] text-body text-muted-foreground">{description}</p>
        ) : null}

        {entry ? (
          <div className="mt-4 max-w-xl">
            <ImportLine entry={entry} />
          </div>
        ) : null}
      </header>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-border">
        <div role="tablist" aria-label={`${title} view`} className="-mb-px flex gap-1">
          <TabButton
            tab="preview"
            active={tab}
            onSelect={setTab}
            panelId={`${id}-panel`}
            count={labels.length}
          >
            Preview
          </TabButton>
          {source ? (
            <TabButton tab="code" active={tab} onSelect={setTab} panelId={`${id}-panel`}>
              Code
            </TabButton>
          ) : null}
        </div>

        {/* Jump row — the examples on this page, one click each. Long components
            (Button has six) otherwise mean scroll-hunting for the one state
            you came to check. */}
        {tab === "preview" && labels.length > 1 ? (
          <nav
            aria-label={`Examples in ${title}`}
            className="flex flex-wrap items-center gap-x-1 gap-y-1 pb-2"
          >
            {labels.map((label) => (
              <a
                key={label}
                href={`#${anchorFor(id, label)}`}
                className="rounded-md px-2 py-1 text-caption text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {label}
              </a>
            ))}
          </nav>
        ) : null}
      </div>

      <div id={`${id}-panel`} role="tabpanel" tabIndex={-1}>
        {tab === "preview" ? (
          <SectionContext.Provider value={ctx}>
            <div className="space-y-8">{children}</div>
          </SectionContext.Provider>
        ) : (
          <FullSource title={title} source={source} entry={entry} />
        )}
      </div>
    </article>
  );
}

function TabButton({
  tab,
  active,
  onSelect,
  panelId,
  count,
  children,
}: {
  tab: Tab;
  active: Tab;
  onSelect: (tab: Tab) => void;
  panelId: string;
  count?: number;
  children: React.ReactNode;
}) {
  const selected = tab === active;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      aria-controls={panelId}
      onClick={() => onSelect(tab)}
      className={cn(
        "inline-flex items-center gap-2 border-b-2 px-3 pb-2.5 pt-1 text-label transition-colors duration-180 ease-smooth-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
        selected
          ? "border-accent text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
      {count ? <span className="font-mono text-[10px] text-muted-foreground">{count}</span> : null}
    </button>
  );
}

/** The Code tab — the whole demo file, which is the runnable version of the page. */
function FullSource({
  title,
  source,
  entry,
}: {
  title: string;
  source: string | undefined;
  entry: DemoEntry | undefined;
}) {
  if (!source) {
    return <p className="text-body text-muted-foreground">No source file found for {title}.</p>;
  }
  return (
    <div className="pg-code">
      <p className="mb-3 font-mono text-caption text-muted-foreground">
        playground/src/demos/{title}.demo.tsx
        {entry ? ` · every example on this page` : null}
      </p>
      <Markdown content={"```tsx\n" + source.trimEnd() + "\n```"} />
    </div>
  );
}

interface VariantBlockProps {
  label: string;
  caption?: string;
  children: React.ReactNode;
}

/**
 * One example on a component page — a labelled axis of variation ("Sizes",
 * "States").
 *
 * The demo renders on a recessed dot-field stage rather than flush against the
 * page, so a component reads as a specimen on a surface: white cards, glass and
 * elevation all need something behind them to be legible at all. Below it, a
 * "Show code" drawer reveals the JSX for *this* example, sliced out of the demo
 * source by `label` — derived, so there is nothing to keep in sync.
 */
export function VariantBlock({ label, caption, children }: VariantBlockProps) {
  const ctx = React.useContext(SectionContext);
  const [open, setOpen] = React.useState(false);

  const snippet = React.useMemo(
    () => (ctx?.source ? extractVariantSource(ctx.source, label) : undefined),
    [ctx?.source, label],
  );

  return (
    <section id={ctx ? anchorFor(ctx.id, label) : undefined} className="scroll-mt-24">
      <div className="mb-2.5 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h2 className="text-label text-foreground">{label}</h2>
        {caption ? <span className="text-caption text-muted-foreground">{caption}</span> : null}
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="pg-stage flex flex-wrap items-start gap-4 p-6">{children}</div>

        {snippet ? (
          <Collapsible open={open} onOpenChange={setOpen}>
            <div className="flex justify-end border-t border-border bg-card px-2 py-1">
              <CollapsibleTrigger
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-mono text-caption text-muted-foreground",
                  "transition-colors duration-180 ease-smooth-out hover:text-foreground",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                )}
              >
                <CodeXmlIcon className="size-3.5" />
                {open ? "Hide code" : "Show code"}
                <ChevronDownIcon
                  className={cn("size-3.5 transition-transform duration-180", open && "rotate-180")}
                />
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <div className="pg-code border-t border-border bg-card p-2">
                <Markdown content={"```tsx\n" + snippet + "\n```"} />
              </div>
            </CollapsibleContent>
          </Collapsible>
        ) : null}
      </div>
    </section>
  );
}
